import type {
  CreateReportBody,
  ObraDetail,
  ObraLedgerState,
  PrevisaoDoEfeito,
  ReportSummary,
  ReportType,
  UpdateReportBody,
} from '@obras/shared';
import {
  BUNNY_STREAM_EXTENSIONS,
  defaultTimezone,
  estadoSemLancamento,
  extensionOf,
  formatBytes,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDays,
  isVideoExtension,
  maskCurrency,
  MAX_VIDEO_BYTES,
  parseCurrencyToCents,
  preverEfeito,
  remainingBalanceCents,
  REPORT_TYPE_META,
  REPORT_TYPE_ORDER,
  todayIn,
} from '@obras/shared';
import * as Icons from 'lucide-react';
import { ArrowLeft, ArrowRight, Check, ShieldAlert, Upload, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState, type DragEvent, type FormEvent } from 'react';

import {
  Bloco,
  CAMPO_DA_ETAPA,
  Dado,
  DESLIZE,
  focarNaEtapa,
  Secao,
} from '../../components/ui/Assistente.js';
import { Button } from '../../components/ui/Button.js';
import { TrilhaDeEtapas, type EtapaDaTrilha } from '../../components/ui/Etapas.js';
import { FormError, Notice } from '../../components/ui/Feedback.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { cn } from '../../lib/cn.js';
import { Selo } from '../documentos/Arquivo.js';
import {
  uploadMedia,
  uploadVideo,
  useCreateReport,
  useUpdateReport,
  type UploadedMedia,
} from './api.js';
import {
  apagarRascunho,
  chaveDoRascunho,
  gravarRascunho,
  lerRascunho,
  type ArquivoGuardado,
  type Rascunho,
} from './rascunho.js';

/**
 * O que o seletor de arquivo oferece.
 *
 * Imagem e PDF vão para o Storage; os contêineres de vídeo vão para o Bunny
 * Stream. A lista de vídeo é a que a biblioteca transcodifica — oferecer mais
 * do que isso só produziria um envio que falha depois de subir.
 */
const ACEITOS = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  ...BUNNY_STREAM_EXTENSIONS.map((extensao) => `.${extensao}`),
].join(',');

const ICON = { strokeWidth: 1.5 } as const;

// ─────────────────────────────────────────────────────────────── etapas

type EtapaId = 'tipo' | 'dados' | 'arquivos' | 'revisao';

/** O relatório novo passa pelas quatro; a correção, só pelos dados e pela revisão. */
const ETAPAS_DO_NOVO: readonly EtapaId[] = ['tipo', 'dados', 'arquivos', 'revisao'];
const ETAPAS_DA_CORRECAO: readonly EtapaId[] = ['dados', 'revisao'];

/**
 * Quantos segundos a revisão fica na tela antes de "Finalizar" ser liberado.
 *
 * Finalizar lança o relatório no histórico e, quando há valor ou prazo, no
 * livro-razão — que não se apaga, só se estorna. A espera garante que a revisão
 * seja lida, e não atravessada com um clique a mais.
 */
const ESPERA_DA_REVISAO = 5;

const ROTULO: Record<EtapaId, string> = {
  tipo: 'Tipo de registro',
  dados: 'Dados',
  arquivos: 'Arquivos',
  revisao: 'Revisão',
};

const CABECALHO: Record<EtapaId, { titulo: string; descricao: string }> = {
  tipo: {
    titulo: 'Tipo de registro',
    descricao:
      'Cada relatório registra um único tipo de ato. O tipo define os campos seguintes e o efeito no contrato.',
  },
  dados: {
    titulo: 'Dados do registro',
    descricao: 'O que foi verificado, quando e, se houver, o valor ou o prazo do ato.',
  },
  arquivos: {
    titulo: 'Fotos, vídeos e documentos',
    descricao:
      'O registro de campo que sustenta o relatório. Cada arquivo sobe assim que é escolhido; anexar é opcional.',
  },
  revisao: {
    titulo: 'Revisão',
    descricao: `Confira tudo antes de finalizar: o botão Finalizar é liberado após ${ESPERA_DA_REVISAO} segundos. Depois de finalizado, o relatório só muda por correção, com motivo.`,
  },
};

const CABECALHO_DA_REVISAO_DA_CORRECAO = {
  titulo: 'Revisão e motivo',
  descricao: `Confira o que muda e registre o motivo: o botão Finalizar é liberado após ${ESPERA_DA_REVISAO} segundos. A correção fica no relatório e na auditoria.`,
};

/** Em que etapa mora cada campo que o servidor pode recusar. */
const ETAPA_DO_CAMPO: Record<string, EtapaId> = {
  type: 'tipo',
  title: 'dados',
  description: 'dados',
  visitDate: 'dados',
  movementDate: 'dados',
  amountCents: 'dados',
  days: 'dados',
  mediaIds: 'arquivos',
  reason: 'revisao',
};

/**
 * Ao abrir o modal, o mesmo, dentro da etapa em tela; sem campo (a revisão), o
 * título da etapa, que o leitor de tela anuncia.
 */
const FOCO_AO_ABRIR: readonly string[] = [
  ...CAMPO_DA_ETAPA.map((seletor) => `[data-etapa] :is(${seletor})`),
  '[data-etapa] [data-titulo-da-etapa]',
];

// ──────────────────────────────────────────────────────────────── tipos

/** A cor de cada tipo: a do marcador da linha do tempo (design.md §4). */
const COR_DO_TIPO: Record<string, string> = {
  muted: 'bg-ink-3',
  success: 'bg-success',
  info: 'bg-info',
  danger: 'bg-danger',
  warning: 'bg-warning',
};

const TEXTO_DO_TOM: Record<string, string> = {
  muted: 'text-ink-3',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

const EXEMPLO_DE_TITULO: Record<ReportType, string> = {
  visita: 'Visita de acompanhamento da cobertura',
  medicao: 'Medição 05 — cobertura e alvenaria',
  aditivo_valor: 'Aditivo 01 — ampliação das salas',
  supressao_valor: 'Supressão 01 — retirada do muro de divisa',
  aditivo_prazo_os: 'Aditivo de prazo da OS 01',
  aditivo_prazo_contrato: 'Aditivo de prazo do contrato 01',
  glosa: 'Glosa do saldo remanescente',
};

const SECAO_DO_ATO: Record<ReportType, string> = {
  visita: '',
  medicao: 'Valor da medição',
  aditivo_valor: 'Valor do aditivo',
  supressao_valor: 'Valor da supressão',
  aditivo_prazo_os: 'Prazo da OS',
  aditivo_prazo_contrato: 'Prazo do contrato',
  glosa: 'Glosa',
};

const COM_VALOR: readonly ReportType[] = ['medicao', 'aditivo_valor', 'supressao_valor'];
const COM_DIAS: readonly ReportType[] = ['aditivo_prazo_os', 'aditivo_prazo_contrato'];

function IconeDoTipo({ tipo, tamanho = 18 }: { tipo: ReportType; tamanho?: number }) {
  const Componente =
    (Icons as unknown as Record<string, Icons.LucideIcon>)[REPORT_TYPE_META[tipo].icon] ??
    Icons.FileText;
  return <Componente size={tamanho} strokeWidth={1.75} aria-hidden />;
}

/** O quadrado colorido do tipo, com o ícone dele: o mesmo da linha do tempo. */
function QuadroDoTipo({ tipo, grande = false }: { tipo: ReportType; grande?: boolean }) {
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center text-on-tone',
        COR_DO_TIPO[REPORT_TYPE_META[tipo].tone] ?? 'bg-ink-3',
        grande ? 'size-11' : 'size-9',
      )}
    >
      <IconeDoTipo tipo={tipo} tamanho={grande ? 20 : 17} />
    </span>
  );
}

/**
 * O que a obra diz de cada tipo: a referência que ajuda a escolher — o saldo, o
 * valor atual, o fim do prazo — e, quando o tipo não cabe nesta obra, por quê.
 * A recusa é a mesma do razão; avisar aqui poupa a pessoa de descobri-la no fim.
 */
function situacaoDoTipo(
  tipo: ReportType,
  estado: ObraLedgerState,
): { referencia: string; bloqueio?: string } {
  const saldo = remainingBalanceCents(estado);

  switch (tipo) {
    case 'visita':
      return { referencia: 'Atualiza a última visita e o prazo da próxima' };
    case 'medicao':
      return saldo > 0
        ? { referencia: `Saldo a pagar: ${formatCurrency(saldo)}` }
        : { referencia: 'Sem saldo a pagar', bloqueio: 'O contrato não tem saldo a pagar.' };
    case 'aditivo_valor':
      return { referencia: `Valor atual: ${formatCurrency(estado.currentValueCents)}` };
    case 'supressao_valor':
      return saldo > 0
        ? { referencia: `Pode reduzir até ${formatCurrency(saldo)}` }
        : {
            referencia: 'Sem saldo a reduzir',
            bloqueio: 'O contrato já foi todo pago ou glosado: não há o que suprimir.',
          };
    case 'aditivo_prazo_os':
      return estado.osEnd
        ? { referencia: `Fim da OS: ${formatDate(estado.osEnd)}` }
        : { referencia: 'Fim da OS não informado', bloqueio: 'A obra não tem data de fim da OS.' };
    case 'aditivo_prazo_contrato':
      return estado.contractEnd
        ? { referencia: `Fim do contrato: ${formatDate(estado.contractEnd)}` }
        : {
            referencia: 'Fim do contrato não informado',
            bloqueio: 'A obra não tem data de fim do contrato.',
          };
    case 'glosa':
      return saldo > 0
        ? { referencia: `Glosa todo o saldo: ${formatCurrency(saldo)}` }
        : { referencia: 'Sem saldo a glosar', bloqueio: 'Não há saldo remanescente para glosar.' };
  }
}

// ───────────────────────────────────────────────────────────── arquivos

interface ArquivoDoRelatorio {
  readonly id: string;
  readonly nome: string;
  readonly tamanho: number;
  /**
   * O arquivo escolhido. Falta no que voltou de um rascunho: dele só ficou a
   * referência da mídia, que já subiu — e é só ela que o relatório leva.
   */
  readonly arquivo?: File | undefined;
  /** Endereço local da imagem, para a miniatura aparecer antes do envio terminar. */
  readonly previa: string | null;
  readonly video: boolean;
  situacao: 'aguardando' | 'enviando' | 'enviado' | 'erro';
  midia?: UploadedMedia | undefined;
  erro?: string | undefined;
}

const plural = (n: number, um: string, varios: string): string => `${n} ${n === 1 ? um : varios}`;

/** O arquivo de um rascunho volta como enviado: a mídia já está no servidor. */
const arquivoDoRascunho = (guardado: ArquivoGuardado): ArquivoDoRelatorio => ({
  id: guardado.id,
  nome: guardado.nome,
  tamanho: guardado.tamanho,
  previa: null,
  video: guardado.video,
  situacao: 'enviado',
  midia: {
    id: guardado.id,
    mime: guardado.mime,
    sizeBytes: guardado.tamanho,
    deduplicated: false,
  },
});

/** Só o que já subiu vai para o rascunho: o que ainda subia precisa ser escolhido de novo. */
const arquivosParaGuardar = (arquivos: readonly ArquivoDoRelatorio[]): ArquivoGuardado[] =>
  arquivos.flatMap((item) =>
    item.situacao === 'enviado' && item.midia
      ? [
          {
            id: item.midia.id,
            nome: item.nome,
            tamanho: item.tamanho,
            mime: item.midia.mime,
            video: item.video,
          },
        ]
      : [],
  );

// ────────────────────────────────────────────────────────────── peças

/**
 * O efeito no contrato, calculado enquanto se preenche (`preverEfeito`): o que
 * era, o que passa a ser e quanto muda. A recusa do razão aparece aqui, antes de
 * lançar, com a mesma mensagem que o servidor daria.
 */
function EfeitoNoContrato({
  previsao,
  tom,
  pedido,
}: {
  previsao: PrevisaoDoEfeito | null;
  /** Tom do tipo, para a variação. */
  tom: string;
  /** O que dizer enquanto falta o valor ou os dias. */
  pedido: string;
}) {
  return (
    <div className="border border-line bg-surface-2">
      <p className="border-b border-line px-4 py-2 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
        Efeito no contrato
      </p>
      <div className="px-4 py-3.5">
        {!previsao || previsao.situacao === 'incompleta' ? (
          <p className="text-[13px] text-ink-3">{pedido}</p>
        ) : previsao.situacao === 'recusada' ? (
          <p role="alert" className="flex items-start gap-2 text-[13px] text-danger">
            <Icons.CircleAlert {...ICON} size={15} className="mt-px shrink-0" aria-hidden />
            {previsao.mensagem}
          </p>
        ) : previsao.situacao === 'sem_efeito' ? (
          <p className="text-[13px] text-ink-3">Não altera valores nem prazos do contrato.</p>
        ) : (
          <>
            <dl className="flex flex-col gap-3">
              {previsao.linhas.map((linha) => (
                <div key={linha.rotulo}>
                  <dt className="text-[12px] text-ink-3">{linha.rotulo}</dt>
                  <dd className="tabular mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[14px]">
                    <span className="text-ink-3">{linha.antes}</span>
                    <ArrowRight size={13} strokeWidth={1.75} className="text-ink-3" aria-hidden />
                    <span className="font-semibold text-ink">{linha.depois}</span>
                    {linha.variacao ? (
                      <span className={cn('text-[12px] font-medium', tom)}>{linha.variacao}</span>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
            {previsao.nota ? (
              <p className="mt-3 border-t border-line pt-3 text-[12px] text-warning">
                {previsao.nota}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Um tipo na grade da primeira etapa: o quadro colorido, o nome, a dica e a
 * referência da obra — o saldo, o valor, o fim do prazo. O escolhido ganha o
 * filete laranja e o visto; o que não cabe nesta obra fica apagado, com o porquê
 * no lugar da referência. Dois cliques escolhem e já avançam.
 */
function CartaoDeTipo({
  tipo,
  ativo,
  referencia,
  bloqueio,
  onEscolher,
  onConfirmar,
}: {
  tipo: ReportType;
  ativo: boolean;
  referencia: string;
  bloqueio?: string | undefined;
  onEscolher: () => void;
  onConfirmar: () => void;
}) {
  const meta = REPORT_TYPE_META[tipo];

  return (
    <button
      type="button"
      role="radio"
      aria-checked={ativo}
      disabled={Boolean(bloqueio)}
      title={bloqueio}
      onClick={onEscolher}
      onDoubleClick={onConfirmar}
      className={cn(
        'flex items-start gap-3.5 border p-4 text-left transition-[border-color,background-color,box-shadow] duration-150',
        ativo
          ? 'border-primary bg-primary-soft/45 shadow-[inset_0_0_0_1px_var(--color-primary)]'
          : 'border-line bg-surface hover:border-line-2 hover:bg-surface-2',
        bloqueio && 'cursor-not-allowed opacity-55 hover:border-line hover:bg-surface',
      )}
    >
      <QuadroDoTipo tipo={tipo} />
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold tracking-tight text-ink">
          {meta.label}
        </span>
        <span className="mt-0.5 block text-[13px] text-ink-3">{meta.hint}</span>
        <span
          className={cn(
            'tabular mt-2.5 block text-[12px]',
            bloqueio ? 'text-danger' : 'text-ink-2',
          )}
        >
          {bloqueio ?? referencia}
        </span>
      </span>
      <span
        aria-hidden
        className={cn(
          'grid size-5 shrink-0 place-items-center border transition-colors duration-150',
          ativo
            ? 'border-primary bg-primary text-on-primary'
            : 'border-line-2 bg-surface text-transparent',
        )}
      >
        <Check size={12} strokeWidth={3} />
      </span>
    </button>
  );
}

/** Um arquivo na etapa de arquivos: a miniatura, ou o selo da extensão, com a situação do envio. */
function QuadroDeArquivo({
  item,
  onRemover,
  onTentarDeNovo,
}: {
  item: ArquivoDoRelatorio;
  onRemover: () => void;
  onTentarDeNovo: () => void;
}) {
  const extensao = extensionOf(item.nome);

  return (
    <li className="group relative aspect-square overflow-hidden border border-line bg-sunken">
      {item.previa ? (
        <img src={item.previa} alt="" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center">
          <span className="flex flex-col items-center gap-2 text-ink-3">
            {item.video ? (
              <Icons.Video {...ICON} size={22} aria-hidden />
            ) : (
              <Icons.FileText {...ICON} size={22} aria-hidden />
            )}
            <Selo extension={extensao} />
          </span>
        </span>
      )}

      {item.situacao === 'enviando' || item.situacao === 'aguardando' ? (
        <span className="absolute inset-0 grid place-items-center bg-black/45 text-white">
          <Icons.Loader2 {...ICON} size={20} className="animate-spin" aria-hidden />
          <span className="sr-only">Enviando {item.nome}</span>
        </span>
      ) : null}

      {item.situacao === 'enviado' ? (
        <span
          title="Enviado"
          className="absolute top-0 left-0 grid size-6 place-items-center bg-success text-on-tone"
        >
          <Check size={13} strokeWidth={2.5} aria-hidden />
          <span className="sr-only">Enviado</span>
        </span>
      ) : null}

      {item.situacao === 'erro' ? (
        <span className="absolute inset-x-0 bottom-0 flex flex-col gap-1 bg-danger px-2 py-1.5 text-[11px] leading-tight text-on-tone">
          <span className="line-clamp-2">{item.erro}</span>
          <button
            type="button"
            onClick={onTentarDeNovo}
            className="self-start font-semibold underline underline-offset-2"
          >
            Tentar de novo
          </button>
        </span>
      ) : (
        <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-2 py-1 text-[11px] text-white">
          {item.nome} · {formatBytes(item.tamanho)}
        </span>
      )}

      <button
        type="button"
        onClick={onRemover}
        aria-label={`Retirar ${item.nome}`}
        title="Retirar"
        className="absolute top-0 right-0 grid size-7 place-items-center bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      >
        <X {...ICON} size={14} aria-hidden />
      </button>
    </li>
  );
}

/**
 * "Finalizar", na revisão: preso por alguns segundos, com a contagem no rótulo
 * e uma barra que enche no pé do botão. Monta de novo a cada chegada à revisão,
 * e a contagem recomeça — quem volta para ajustar um dado confere outra vez.
 */
function BotaoFinalizar({
  bloqueio,
  salvando,
  onFinalizar,
}: {
  /** Por que não dá para finalizar, passada a espera: o botão diz, em vez de ficar mudo. */
  bloqueio: string | null;
  salvando: boolean;
  onFinalizar: () => void;
}) {
  const [restante, setRestante] = useState(ESPERA_DA_REVISAO);

  useEffect(() => {
    if (restante <= 0) return undefined;
    const relogio = window.setTimeout(() => setRestante((atual) => atual - 1), 1000);
    return () => window.clearTimeout(relogio);
  }, [restante]);

  const esperando = restante > 0;

  return (
    <span className="relative inline-flex">
      <Button
        variant="primary"
        loading={salvando}
        disabled={esperando || bloqueio !== null}
        title={
          esperando
            ? `Confira a revisão: o botão é liberado em ${restante} s.`
            : (bloqueio ?? undefined)
        }
        icon={<Check {...ICON} size={16} />}
        onClick={onFinalizar}
        className="tabular min-w-[8.5rem]"
      >
        {esperando ? `Finalizar (${restante})` : 'Finalizar'}
      </Button>
      {esperando ? (
        <motion.span
          aria-hidden
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: ESPERA_DA_REVISAO, ease: 'linear' }}
          className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-left bg-primary"
        />
      ) : null}
      <span aria-live="polite" className="sr-only">
        {esperando ? '' : 'Botão Finalizar liberado.'}
      </span>
    </span>
  );
}

// ──────────────────────────────────────────────────────────── o modal

/**
 * Registrar relatório, em etapas — ou corrigir um já publicado (ADR 060).
 *
 * Um assistente, e não um formulário de rolo: **tipo**, **dados**, **arquivos**
 * e **revisão**, nessa ordem, porque o tipo decide os campos e o efeito no
 * contrato. A trilha à esquerda mostra onde se está e o que cada etapa já tem;
 * volta-se a qualquer etapa alcançada, e avança-se por "Continuar", que só deixa
 * passar a etapa completa — o campo que falta ganha a mensagem e o foco.
 *
 * Os dados mostram, enquanto se preenche, o **efeito no contrato** — o pago, o
 * valor, o saldo, o fim do prazo, antes e depois —, com a regra do próprio razão:
 * o valor que o servidor recusaria é recusado aqui, com a mesma mensagem. A
 * revisão junta tudo, e "Finalizar" só é liberado depois de alguns segundos nela.
 *
 * Nada se perde por um fechar sem querer. Com algo escrito, `Esc`, o clique fora,
 * o "×" e "Cancelar" pedem confirmação, com o foco em continuar; e o relatório
 * novo vai sendo guardado como rascunho neste navegador (`rascunho.ts`), que volta
 * ao reabrir — inclusive depois de recarregar a página.
 *
 * Com `report`, é a correção: o tipo fica travado, os arquivos não entram — a
 * correção é do que foi escrito e lançado —, e a revisão mostra o que muda e
 * pede o motivo. Se o valor, os dias ou a data do ato mudarem, o servidor estorna
 * o lançamento antigo e lança o novo, no mesmo ato. A correção não vira rascunho:
 * sair dela, confirmado, descarta o que foi alterado.
 */
export function ReportFormModal({
  open,
  onClose,
  obra,
  report = null,
}: {
  open: boolean;
  onClose: () => void;
  obra: ObraDetail;
  /** O relatório publicado a corrigir; sem ele, o formulário registra um novo. */
  report?: ReportSummary | null | undefined;
}) {
  const { user, organization } = useAuth();
  const create = useCreateReport(obra.id);
  const update = useUpdateReport(obra.id);
  const formRef = useRef<HTMLFormElement>(null);
  const inputDeArquivos = useRef<HTMLInputElement>(null);
  /** A etapa que acabou de entrar leva o foco ao primeiro campo — só depois de uma troca. */
  const focarAoEntrar = useRef(false);
  /** As miniaturas locais, para devolver a memória quando o modal sai de cena. */
  const previas = useRef(new Set<string>());

  const corrigindo = report !== null;
  const etapas = corrigindo ? ETAPAS_DA_CORRECAO : ETAPAS_DO_NOVO;

  /*
   * O rascunho é de quem escreve, na obra em que escreve: a chave leva o órgão,
   * a pessoa e a obra. A página monta o formulário de novo a cada obra (`key`),
   * e é na montagem que o rascunho é lido.
   */
  const chave = chaveDoRascunho(organization?.id ?? '-', user?.id ?? '-', obra.id);
  const [rascunho] = useState<Rascunho | null>(() => (corrigindo ? null : lerRascunho(chave)));
  /** Sem tipo não se passa da primeira etapa — nem num rascunho adulterado. */
  const etapaDoRascunho = rascunho?.tipo ? rascunho.indice : 0;

  const [indice, setIndice] = useState(etapaDoRascunho);
  const [alcancada, setAlcancada] = useState(
    rascunho?.tipo ? Math.max(rascunho.alcancada, etapaDoRascunho) : 0,
  );
  const [direcao, setDirecao] = useState(1);
  const [tipo, setTipo] = useState<ReportType | null>(rascunho?.tipo ?? null);
  const [titulo, setTitulo] = useState(rascunho?.titulo ?? '');
  const [descricao, setDescricao] = useState(rascunho?.descricao ?? '');
  const [dataDaVisita, setDataDaVisita] = useState(
    () => rascunho?.dataDaVisita || todayIn(defaultTimezone()),
  );
  const [dataDoAto, setDataDoAto] = useState(rascunho?.dataDoAto ?? '');
  const [valor, setValor] = useState(rascunho?.valor ?? '');
  const [dias, setDias] = useState(rascunho?.dias ?? '');
  const [motivo, setMotivo] = useState('');
  const [arquivos, setArquivos] = useState<ArquivoDoRelatorio[]>(
    () => rascunho?.arquivos.map(arquivoDoRascunho) ?? [],
  );
  /** Etapas em que "Continuar" já foi tentado: dali em diante, os erros aparecem. */
  const [tentou, setTentou] = useState<ReadonlySet<EtapaId>>(() => new Set());
  const [errosDoServidor, setErrosDoServidor] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);
  /** Quando o rascunho em tela foi guardado: o aviso de que o preenchimento continua. */
  const [recuperadoEm, setRecuperadoEm] = useState<string | null>(rascunho?.salvoEm ?? null);
  /** A confirmação de saída, aberta por cima do assistente. */
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);

  /*
   * Quantas vezes o modal já abriu. O formulário continua montado com o modal
   * fechado, e a espera do "Finalizar" corria escondida: quem reabria um
   * rascunho parado na revisão encontrava o botão já liberado. A contagem entra
   * na chave do botão, e cada abertura começa a espera do zero.
   */
  const [abertoAntes, setAbertoAntes] = useState(open);
  const [aberturas, setAberturas] = useState(0);
  if (open !== abertoAntes) {
    setAbertoAntes(open);
    if (open) setAberturas((vezes) => vezes + 1);
  }

  /*
   * O relatório carregado na correção. Abrir a correção de outro relatório
   * troca o conteúdo — ajustado na renderização, não num efeito, para o
   * formulário nunca aparecer com o texto do anterior.
   */
  const [carregado, setCarregado] = useState<string | null>(null);
  if (open && report && report.id !== carregado) {
    const lancamento = report.movement;
    setCarregado(report.id);
    setIndice(0);
    setAlcancada(0);
    setDirecao(1);
    setTipo(report.type);
    setTitulo(report.title);
    setDescricao(report.description ?? '');
    setDataDaVisita(report.visitDate);
    setDataDoAto(report.movementDate ?? '');
    setValor(
      lancamento && lancamento.days === null
        ? formatCurrency(Math.abs(lancamento.amountCents))
        : '',
    );
    setDias(lancamento?.days ? String(lancamento.days) : '');
    setMotivo('');
    setTentou(new Set());
    setErrosDoServidor({});
    setErroGeral(null);
  }
  if (!open && carregado !== null) setCarregado(null);

  useEffect(() => {
    const conjunto = previas.current;
    return () => {
      for (const url of conjunto) URL.revokeObjectURL(url);
    };
  }, []);

  // ── o que se calcula do que foi preenchido

  const etapaId: EtapaId = etapas[indice] ?? etapas[0] ?? 'dados';
  const ultima = indice === etapas.length - 1;
  const meta = tipo ? REPORT_TYPE_META[tipo] : null;
  const comValor = tipo !== null && COM_VALOR.includes(tipo);
  const comDias = tipo !== null && COM_DIAS.includes(tipo);
  const tom = meta ? (TEXTO_DO_TOM[meta.tone] ?? 'text-ink') : 'text-ink';

  const estadoDaObra: ObraLedgerState = {
    currentValueCents: obra.currentValueCents,
    paidValueCents: obra.paidValueCents,
    glossValueCents: obra.glossValueCents,
    contractEnd: obra.contractEnd,
    osEnd: obra.osEnd,
  };

  /** O lançamento do relatório corrigido ainda vale: não foi estornado à parte. */
  const vale = report
    ? report.movement !== null && report.movement.reversedByMovementId === null
    : true;
  /** Na correção, a conta parte do estado sem o lançamento que vai ser estornado. */
  const estadoDePartida =
    report?.movement && vale ? estadoSemLancamento(estadoDaObra, report.movement) : estadoDaObra;

  const valorEmCentavos = parseCurrencyToCents(valor) ?? undefined;
  const diasEmNumero = dias.trim() === '' ? undefined : Number(dias);

  const previsao: PrevisaoDoEfeito | null =
    tipo && (!corrigindo || vale)
      ? preverEfeito(
          estadoDePartida,
          {
            type: tipo,
            amountCents: comValor ? valorEmCentavos : undefined,
            days: comDias ? diasEmNumero : undefined,
            effectiveDate: dataDoAto || undefined,
          },
          corrigindo ? { comparadoA: estadoDaObra } : {},
        )
      : null;

  const enviando = arquivos.some(
    (item) => item.situacao === 'enviando' || item.situacao === 'aguardando',
  );
  const enviados = arquivos.filter((item) => item.situacao === 'enviado');
  const comErroNoEnvio = arquivos.filter((item) => item.situacao === 'erro');

  const naoResponde =
    !corrigindo && obra.responsibleFiscal != null && obra.responsibleFiscal.id !== user?.id;

  /** O que muda na correção, campo a campo, para a revisão. */
  const mudancas: { rotulo: string; antes: string; depois: string }[] = [];
  let lancamentoRefeito = false;
  if (report) {
    const descricaoNova = descricao.trim();
    if (report.title !== titulo.trim()) {
      mudancas.push({ rotulo: 'Título', antes: report.title, depois: titulo.trim() });
    }
    if ((report.description ?? '').trim() !== descricaoNova) {
      mudancas.push({
        rotulo: 'Descrição',
        antes: report.description?.trim() || 'Sem descrição',
        depois: descricaoNova || 'Sem descrição',
      });
    }
    if (report.visitDate !== dataDaVisita) {
      mudancas.push({
        rotulo: 'Data da visita',
        antes: formatDate(report.visitDate),
        depois: formatDate(dataDaVisita),
      });
    }
    if (report.type !== 'visita' && (report.movementDate ?? '') !== dataDoAto) {
      mudancas.push({
        rotulo: 'Data da movimentação',
        antes: formatDate(report.movementDate),
        depois: formatDate(dataDoAto || null),
      });
    }
    const lancamento = report.movement;
    if (lancamento && vale) {
      if (comValor && valorEmCentavos !== Math.abs(lancamento.amountCents)) {
        mudancas.push({
          rotulo: 'Valor',
          antes: formatCurrency(Math.abs(lancamento.amountCents)),
          depois: valorEmCentavos !== undefined ? formatCurrency(valorEmCentavos) : '—',
        });
      }
      if (comDias && diasEmNumero !== lancamento.days) {
        mudancas.push({
          rotulo: 'Dias',
          antes: formatDays(lancamento.days ?? 0),
          depois: diasEmNumero !== undefined ? formatDays(diasEmNumero) : '—',
        });
      }
      lancamentoRefeito =
        (dataDoAto || dataDaVisita) !== lancamento.effectiveDate ||
        (comValor && valorEmCentavos !== Math.abs(lancamento.amountCents)) ||
        (comDias && diasEmNumero !== lancamento.days);
    }
  }

  // ── o rascunho

  /**
   * Há o que perder? Na correção, qualquer alteração ou o motivo; no relatório
   * novo, o que foi escrito ou anexado. Escolher o tipo, sozinho, não conta: é um
   * clique, e refazê-lo custa menos que responder a uma confirmação.
   */
  const preenchido = corrigindo
    ? mudancas.length > 0 || motivo.trim() !== ''
    : titulo.trim() !== '' ||
      descricao.trim() !== '' ||
      dataDoAto !== '' ||
      valor !== '' ||
      dias !== '' ||
      arquivos.length > 0;

  /*
   * O relatório novo vai para o rascunho a cada mudança — e só a cada mudança:
   * abrir a página não regrava nada, para o horário do rascunho continuar sendo
   * o da última vez em que alguém escreveu. A primeira passada só anota o que já
   * está em tela, que é o próprio rascunho lido (ou nada).
   */
  const guardado = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (corrigindo) return;

    const conteudo = preenchido
      ? {
          tipo,
          titulo,
          descricao,
          dataDaVisita,
          dataDoAto,
          valor,
          dias,
          indice,
          alcancada,
          arquivos: arquivosParaGuardar(arquivos),
        }
      : null;
    const assinatura = conteudo ? JSON.stringify(conteudo) : null;

    if (guardado.current === undefined || assinatura === guardado.current) {
      guardado.current = assinatura;
      return;
    }
    guardado.current = assinatura;

    if (conteudo)
      gravarRascunho(chave, { versao: 1, salvoEm: new Date().toISOString(), ...conteudo });
    else apagarRascunho(chave);
  }, [
    chave,
    corrigindo,
    preenchido,
    tipo,
    titulo,
    descricao,
    dataDaVisita,
    dataDoAto,
    valor,
    dias,
    indice,
    alcancada,
    arquivos,
  ]);

  // ── o que falta, por etapa

  function errosDaEtapa(id: EtapaId): Record<string, string> {
    const erros: Record<string, string> = {};

    if (id === 'tipo' && !tipo) erros['type'] = 'Escolha o tipo de registro para continuar.';

    if (id === 'dados') {
      if (titulo.trim().length < 3) {
        erros['title'] = 'Informe um título com pelo menos 3 caracteres.';
      }
      if (!dataDaVisita) erros['visitDate'] = 'Informe a data da visita.';
      if (tipo && tipo !== 'visita' && !dataDoAto) {
        erros['movementDate'] = 'Informe a data da movimentação.';
      }
      if (
        comDias &&
        diasEmNumero !== undefined &&
        (!Number.isInteger(diasEmNumero) || diasEmNumero < 1 || diasEmNumero > 3650)
      ) {
        erros['days'] = 'Informe de 1 a 3.650 dias.';
      }
      if (previsao?.situacao === 'incompleta' && !erros[previsao.campo]) {
        erros[previsao.campo] =
          previsao.campo === 'days' ? 'Informe a quantidade de dias.' : 'Informe o valor.';
      }
      if (previsao?.situacao === 'recusada' && !erros[previsao.campo]) {
        erros[previsao.campo] = previsao.mensagem;
      }
    }

    if (id === 'revisao' && corrigindo && motivo.trim().length < 10) {
      erros['reason'] = 'Explique o motivo da correção, com pelo menos 10 caracteres.';
    }

    return erros;
  }

  const temErros = (id: EtapaId): boolean => Object.keys(errosDaEtapa(id)).length > 0;
  const temErroDoServidor = (id: EtapaId): boolean =>
    Object.keys(errosDoServidor).some((campo) => ETAPA_DO_CAMPO[campo] === id);

  const errosVisiveis: Record<string, string> = {
    ...errosDoServidor,
    ...(tentou.has(etapaId) ? errosDaEtapa(etapaId) : {}),
  };
  const erroDe = (campo: string) => (errosVisiveis[campo] ? { error: errosVisiveis[campo] } : {});

  /** Mexer num campo tira dele a recusa do servidor: ela falava do valor antigo. */
  function mexeu(campo: string): void {
    setErrosDoServidor((atual) => {
      if (!(campo in atual)) return atual;
      const resto = { ...atual };
      delete resto[campo];
      return resto;
    });
  }

  // ── navegação

  function irPara(destino: number): void {
    if (destino === indice || destino < 0 || destino >= etapas.length) return;
    setDirecao(destino > indice ? 1 : -1);
    setIndice(destino);
    setAlcancada((atual) => Math.max(atual, destino));
    setErroGeral(null);
    // O aviso do rascunho recuperado já foi visto: não acompanha a pessoa pelas etapas.
    setRecuperadoEm(null);
    focarAoEntrar.current = true;
  }

  function focarPrimeiroErro(): void {
    requestAnimationFrame(() => {
      formRef.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"], [role="radio"]:not(:disabled)')
        ?.focus();
    });
  }

  /** Marca a etapa como tentada, vai até ela e põe o foco no primeiro problema. */
  function mostrarPendencia(id: EtapaId): void {
    setTentou((atual) => new Set(atual).add(id));
    const destino = etapas.indexOf(id);
    if (destino === indice) focarPrimeiroErro();
    else irPara(destino);
  }

  function avancar(): void {
    if (temErros(etapaId)) {
      mostrarPendencia(etapaId);
      return;
    }
    irPara(indice + 1);
  }

  /** Pela trilha, volta-se livre; para a frente, só passando por etapas completas. */
  function irPelaTrilha(destino: number): void {
    if (destino < indice) {
      irPara(destino);
      return;
    }
    const pendente = etapas.slice(0, destino).find((id) => temErros(id));
    if (pendente) mostrarPendencia(pendente);
    else irPara(destino);
  }

  // ── arquivos

  function atualizarArquivo(id: string, mudanca: Partial<ArquivoDoRelatorio>): void {
    setArquivos((atual) => atual.map((item) => (item.id === id ? { ...item, ...mudanca } : item)));
  }

  async function enviar(item: ArquivoDoRelatorio): Promise<void> {
    const { arquivo } = item;
    // O que voltou do rascunho já subiu: não há arquivo para mandar de novo.
    if (!arquivo) return;

    atualizarArquivo(item.id, { situacao: 'enviando', erro: undefined });

    try {
      if (item.video && arquivo.size > MAX_VIDEO_BYTES) {
        throw new ApiError(
          'PAYLOAD_TOO_LARGE',
          `Vídeo maior que ${Math.round(MAX_VIDEO_BYTES / (1024 * 1024))} MB.`,
        );
      }

      const midia = item.video ? await uploadVideo(arquivo) : await uploadMedia(arquivo);
      atualizarArquivo(item.id, { situacao: 'enviado', midia });
    } catch (error) {
      atualizarArquivo(item.id, {
        situacao: 'erro',
        erro: error instanceof ApiError ? error.message : 'Falha ao enviar. Tente de novo.',
      });
    }
  }

  /** Cada arquivo sobe isolado, um depois do outro: uma falha não derruba os demais. */
  async function adicionar(lista: FileList | null): Promise<void> {
    if (!lista || lista.length === 0) return;

    const novos: ArquivoDoRelatorio[] = Array.from(lista).map((arquivo) => {
      const previa = arquivo.type.startsWith('image/') ? URL.createObjectURL(arquivo) : null;
      if (previa) previas.current.add(previa);

      return {
        id: `${arquivo.name}-${arquivo.size}-${Math.random().toString(36).slice(2)}`,
        nome: arquivo.name,
        tamanho: arquivo.size,
        arquivo,
        previa,
        video: isVideoExtension(extensionOf(arquivo.name)),
        situacao: 'aguardando',
      };
    });

    setArquivos((atual) => [...atual, ...novos]);
    mexeu('mediaIds');
    for (const item of novos) await enviar(item);
  }

  function retirar(item: ArquivoDoRelatorio): void {
    if (item.previa) {
      URL.revokeObjectURL(item.previa);
      previas.current.delete(item.previa);
    }
    setArquivos((atual) => atual.filter((outro) => outro.id !== item.id));
  }

  function soltar(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setArrastando(false);
    void adicionar(evento.dataTransfer.files);
  }

  // ── lançar

  function recomecar(): void {
    for (const item of arquivos) {
      if (item.previa) {
        URL.revokeObjectURL(item.previa);
        previas.current.delete(item.previa);
      }
    }
    setIndice(0);
    setAlcancada(0);
    setDirecao(1);
    setTipo(null);
    setTitulo('');
    setDescricao('');
    setDataDaVisita(todayIn(defaultTimezone()));
    setDataDoAto('');
    setValor('');
    setDias('');
    setMotivo('');
    setArquivos([]);
    setTentou(new Set());
    setErrosDoServidor({});
    setErroGeral(null);
    setRecuperadoEm(null);
  }

  // ── fechar

  /*
   * Todo caminho de saída passa por aqui: o "×", o `Esc`, o clique fora e o
   * "Cancelar". Sem nada escrito, fecha e recomeça; com algo escrito, pergunta
   * antes — e a pergunta abre com o foco em continuar, o que não perde nada.
   */
  function pedirParaFechar(): void {
    if (salvando) return;
    if (preenchido) {
      setConfirmandoSaida(true);
      return;
    }
    if (!corrigindo) recomecar();
    onClose();
  }

  /** Sai e deixa o rascunho onde está: reabrir continua dele. */
  function sairGuardando(): void {
    setConfirmandoSaida(false);
    setRecuperadoEm(new Date().toISOString());
    onClose();
  }

  /** Sai e joga fora o que foi preenchido — o rascunho, ou as alterações da correção. */
  function descartar(): void {
    setConfirmandoSaida(false);
    if (!corrigindo) recomecar();
    onClose();
  }

  /*
   * O navegador fechou sem perguntar: o pedido de fechar se repetiu sem clique
   * nem tecla no meio — o terceiro `Esc` seguido, o "voltar" do celular. Não há
   * como confirmar, então o relatório novo sai como em "Sair e guardar"; a
   * correção, que não vira rascunho, sai sem salvar.
   */
  function fechadoPeloNavegador(): void {
    setConfirmandoSaida(false);
    if (!corrigindo) {
      if (preenchido) setRecuperadoEm(new Date().toISOString());
      else recomecar();
    }
    onClose();
  }

  /** "Descartar rascunho", no aviso: volta à primeira etapa, em branco, sem fechar. */
  function descartarRascunho(): void {
    recomecar();
    setDirecao(-1);
    focarAoEntrar.current = true;
  }

  function mostrarRecusa(error: unknown, padrao: string): void {
    if (error instanceof ApiError && error.details) {
      const campos = error.fieldErrors();
      setErrosDoServidor(campos);
      setErroGeral('Verifique os campos destacados.');
      // Salta para a primeira etapa com campo recusado.
      const primeira = etapas.find((id) =>
        Object.keys(campos).some((campo) => ETAPA_DO_CAMPO[campo] === id),
      );
      if (primeira && primeira !== etapaId) irPara(etapas.indexOf(primeira));
      return;
    }
    setErroGeral(error instanceof ApiError ? error.message : padrao);
  }

  /** O porquê de "Finalizar" não poder ser apertado agora — o botão diz, em vez de ficar mudo. */
  const bloqueioDoLancamento = enviando
    ? 'Aguarde os arquivos terminarem de subir.'
    : corrigindo && mudancas.length === 0
      ? 'Nada foi alterado no relatório.'
      : null;

  async function lancar(): Promise<void> {
    const pendente = etapas.find((id) => temErros(id));
    if (pendente) {
      mostrarPendencia(pendente);
      return;
    }
    if (bloqueioDoLancamento) return;

    setErroGeral(null);
    setErrosDoServidor({});

    if (report) {
      const corpo: UpdateReportBody = {
        title: titulo.trim(),
        // Apagar a descrição é mandá-la vazia: `null` diz ao servidor que é de propósito.
        description: descricao.trim() === '' ? null : descricao,
        visitDate: dataDaVisita,
        ...(report.type === 'visita' ? {} : { movementDate: dataDoAto || undefined }),
        // Lançamento estornado à parte não tem valor a corrigir.
        ...(comValor && vale ? { amountCents: valorEmCentavos } : {}),
        ...(comDias && vale ? { days: diasEmNumero } : {}),
        reason: motivo.trim(),
      };

      try {
        await update.mutateAsync({ reportId: report.id, body: corpo });
        onClose();
      } catch (error) {
        mostrarRecusa(error, 'Não foi possível salvar a correção.');
      }
      return;
    }

    if (!tipo) return;

    const corpo: CreateReportBody = {
      type: tipo,
      title: titulo.trim(),
      description: descricao.trim() === '' ? undefined : descricao,
      visitDate: dataDaVisita,
      movementDate: tipo === 'visita' ? undefined : dataDoAto || undefined,
      // A glosa não recebe valor: o servidor usa o saldo remanescente.
      amountCents: comValor ? valorEmCentavos : undefined,
      days: comDias ? diasEmNumero : undefined,
      mediaIds: enviados.flatMap((item) => (item.midia ? [item.midia.id] : [])),
      status: 'publicado',
    };

    try {
      await create.mutateAsync(corpo);
      // Lançado, o rascunho cumpriu o papel.
      apagarRascunho(chave);
      guardado.current = null;
      recomecar();
      onClose();
    } catch (error) {
      mostrarRecusa(error, 'Não foi possível lançar o relatório.');
    }
  }

  function enviarFormulario(evento: FormEvent): void {
    evento.preventDefault();
    /*
     * Enter num campo avança a etapa. Na revisão, não faz nada: finalizar é só
     * pelo botão, depois da espera — um Enter a mais não lança o relatório.
     */
    if (!ultima) avancar();
  }

  const salvando = create.isPending || update.isPending;

  // ── a trilha

  const resumoDa = (id: EtapaId, posicao: number): string | undefined => {
    if (id === 'tipo') return meta?.label;
    if (id === 'dados') return titulo.trim() || undefined;
    if (id === 'arquivos') {
      if (arquivos.length > 0) return plural(arquivos.length, 'arquivo', 'arquivos');
      return posicao < alcancada ? 'Nenhum arquivo' : undefined;
    }
    return undefined;
  };

  const trilha: EtapaDaTrilha[] = etapas.map((id, posicao) => ({
    id,
    rotulo: id === 'revisao' && corrigindo ? 'Revisão e motivo' : ROTULO[id],
    resumo: resumoDa(id, posicao),
    feita: posicao < alcancada && !temErros(id),
    alcancavel: posicao <= alcancada,
    comErro: temErroDoServidor(id) || (tentou.has(id) && temErros(id)),
  }));

  const cabecalho =
    etapaId === 'revisao' && corrigindo ? CABECALHO_DA_REVISAO_DA_CORRECAO : CABECALHO[etapaId];

  // ── as etapas

  const etapaTipo = (
    <>
      {naoResponde ? (
        <Notice tone="primary" icon={ShieldAlert}>
          Você não é o fiscal responsável por esta obra. O relatório será registrado em substituição
          a <strong className="font-semibold">{obra.responsibleFiscal?.name}</strong>.
        </Notice>
      ) : null}

      <div role="radiogroup" aria-label="Tipo de registro" className="grid gap-2 sm:grid-cols-2">
        {REPORT_TYPE_ORDER.map((opcao) => {
          const { referencia, bloqueio } = situacaoDoTipo(opcao, estadoDaObra);
          return (
            <CartaoDeTipo
              key={opcao}
              tipo={opcao}
              ativo={tipo === opcao}
              referencia={referencia}
              bloqueio={bloqueio}
              onEscolher={() => {
                setTipo(opcao);
                mexeu('type');
              }}
              onConfirmar={() => {
                setTipo(opcao);
                irPara(indice + 1);
              }}
            />
          );
        })}
      </div>

      {errosVisiveis['type'] ? (
        <p role="alert" className="text-[13px] text-danger">
          {errosVisiveis['type']}
        </p>
      ) : null}
    </>
  );

  const referenciaDoValor =
    tipo === 'medicao'
      ? `Saldo a pagar: ${formatCurrency(remainingBalanceCents(estadoDePartida))}`
      : tipo === 'aditivo_valor'
        ? `Valor atual do contrato: ${formatCurrency(estadoDaObra.currentValueCents)}`
        : tipo === 'supressao_valor'
          ? `Pode reduzir até ${formatCurrency(remainingBalanceCents(estadoDePartida))}`
          : undefined;

  const referenciaDosDias =
    tipo === 'aditivo_prazo_os'
      ? `Fim atual da OS: ${formatDate(estadoDaObra.osEnd)}`
      : tipo === 'aditivo_prazo_contrato'
        ? `Fim atual do contrato: ${formatDate(estadoDaObra.contractEnd)}`
        : undefined;

  const etapaDados = tipo ? (
    <>
      {corrigindo ? (
        <div className="flex items-center gap-3 border border-line bg-surface-2 px-4 py-3">
          <QuadroDoTipo tipo={tipo} />
          <p className="min-w-0 flex-1 text-[13px] text-ink-3">
            <strong className="font-semibold text-ink">{REPORT_TYPE_META[tipo].label}</strong>,
            registrado em {formatDate(report?.createdAt ?? null)}. O tipo não muda na correção: para
            outro tipo, registre outro relatório.
          </p>
        </div>
      ) : null}

      <Secao titulo="Identificação">
        <TextField
          label="Título"
          required
          placeholder={EXEMPLO_DE_TITULO[tipo]}
          value={titulo}
          onChange={(evento) => {
            setTitulo(evento.target.value);
            mexeu('title');
          }}
          {...erroDe('title')}
        />
        <TextAreaField
          label="Descrição"
          rows={5}
          hint="Serviços executados, o que foi verificado em campo e observações."
          value={descricao}
          onChange={(evento) => {
            setDescricao(evento.target.value);
            mexeu('description');
          }}
          {...erroDe('description')}
        />
      </Secao>

      <Secao titulo="Datas">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Data da visita"
            type="date"
            mono
            required
            hint="Quando a fiscalização esteve na obra. A próxima visita passa a contar desta data."
            value={dataDaVisita}
            onChange={(evento) => {
              setDataDaVisita(evento.target.value);
              mexeu('visitDate');
            }}
            {...erroDe('visitDate')}
          />
          {tipo !== 'visita' ? (
            <TextField
              label="Data da movimentação"
              type="date"
              mono
              required
              hint="Data do ato: assinatura da medição ou do aditivo. Pode ser diferente da visita."
              value={dataDoAto}
              onChange={(evento) => {
                setDataDoAto(evento.target.value);
                mexeu('movementDate');
              }}
              {...erroDe('movementDate')}
            />
          ) : null}
        </div>
      </Secao>

      {tipo !== 'visita' ? (
        <Secao titulo={SECAO_DO_ATO[tipo]}>
          {corrigindo && !vale ? (
            <p className="bg-sunken px-4 py-3 text-[13px] text-ink-3">
              O lançamento deste relatório já foi estornado, e o valor não se corrige por aqui. Para
              lançar de novo, registre um relatório novo.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] sm:items-start">
              <div className="flex flex-col gap-4">
                {comValor ? (
                  <TextField
                    label="Valor"
                    mono
                    required
                    inputMode="numeric"
                    placeholder="R$ 0,00"
                    hint={referenciaDoValor}
                    value={valor}
                    onChange={(evento) => {
                      setValor(maskCurrency(evento.target.value));
                      mexeu('amountCents');
                    }}
                    {...erroDe('amountCents')}
                  />
                ) : null}

                {comDias ? (
                  <TextField
                    label="Quantidade de dias"
                    type="number"
                    min={1}
                    max={3650}
                    mono
                    required
                    placeholder="0"
                    hint={referenciaDosDias}
                    value={dias}
                    onChange={(evento) => {
                      setDias(evento.target.value);
                      mexeu('days');
                    }}
                    {...erroDe('days')}
                  />
                ) : null}

                {tipo === 'glosa' ? (
                  <div className="border border-warning/30 bg-warning-soft px-4 py-3.5">
                    <p className="text-[12px] font-medium text-warning">Valor da glosa</p>
                    <p className="numeral mt-1.5 text-[24px] leading-none text-ink">
                      {formatCurrency(remainingBalanceCents(estadoDePartida))}
                    </p>
                    <p className="mt-2 text-[12px] text-ink-3">
                      É todo o saldo a pagar do contrato: não se escolhe nem se altera.
                    </p>
                  </div>
                ) : null}
              </div>

              <EfeitoNoContrato
                previsao={previsao}
                tom={tom}
                pedido={
                  comDias
                    ? 'Informe os dias para ver o novo fim do prazo.'
                    : 'Informe o valor para ver o efeito no contrato.'
                }
              />
            </div>
          )}
        </Secao>
      ) : null}
    </>
  ) : null;

  const etapaArquivos = (
    <>
      <div
        onDragOver={(evento) => {
          evento.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={soltar}
        className={cn(
          'flex flex-col items-center gap-3 border border-dashed px-6 py-8 text-center transition-colors duration-150',
          arrastando ? 'border-primary bg-primary-soft/45' : 'border-line-2 bg-surface-2',
        )}
      >
        <span className="grid size-11 place-items-center border border-line bg-surface text-ink-3">
          <Upload {...ICON} size={20} aria-hidden />
        </span>
        <div>
          <p className="text-[14px] font-medium text-ink">
            Arraste fotos, vídeos e documentos para cá
          </p>
          <p className="mt-1 text-[13px] text-ink-3">
            Fotos em JPG, PNG ou WebP, documentos em PDF e vídeos. A data e a coordenada das fotos
            são preservadas.
          </p>
        </div>
        <Button
          size="sm"
          icon={<Upload {...ICON} size={14} />}
          onClick={() => inputDeArquivos.current?.click()}
        >
          Escolher arquivos
        </Button>
        <input
          ref={inputDeArquivos}
          type="file"
          multiple
          accept={ACEITOS}
          className="hidden"
          onChange={(evento) => {
            void adicionar(evento.target.files);
            evento.target.value = '';
          }}
        />
      </div>

      {arquivos.length > 0 ? (
        <div className="flex flex-col gap-2.5">
          <p className="text-[13px] text-ink-3">
            {plural(arquivos.length, 'arquivo', 'arquivos')}
            {enviados.length > 0 ? ` · ${plural(enviados.length, 'enviado', 'enviados')}` : ''}
            {enviando ? ' · enviando…' : ''}
            {comErroNoEnvio.length > 0 ? ` · ${comErroNoEnvio.length} com falha` : ''}
          </p>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {arquivos.map((item) => (
              <QuadroDeArquivo
                key={item.id}
                item={item}
                onRemover={() => retirar(item)}
                onTentarDeNovo={() => void enviar(item)}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {errosVisiveis['mediaIds'] ? (
        <p role="alert" className="text-[13px] text-danger">
          {errosVisiveis['mediaIds']}
        </p>
      ) : null}
    </>
  );

  const etapaRevisao = report ? (
    <>
      <Bloco titulo="O que muda" onAlterar={() => irPara(0)}>
        {mudancas.length === 0 ? (
          <p className="text-[13px] text-ink-3">
            Nada foi alterado ainda. Volte aos dados para corrigir o que estiver errado.
          </p>
        ) : (
          <dl className="flex flex-col gap-3">
            {mudancas.map((mudanca) => (
              <div key={mudanca.rotulo}>
                <dt className="text-[12px] text-ink-3">{mudanca.rotulo}</dt>
                <dd className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-[14px]">
                  <span className="line-clamp-2 text-ink-3 line-through decoration-ink-3/50">
                    {mudanca.antes}
                  </span>
                  <ArrowRight size={13} strokeWidth={1.75} className="text-ink-3" aria-hidden />
                  <span className="line-clamp-2 font-medium text-ink">{mudanca.depois}</span>
                </dd>
              </div>
            ))}
          </dl>
        )}
      </Bloco>

      {lancamentoRefeito ? (
        <>
          <EfeitoNoContrato
            previsao={previsao}
            tom={tom}
            pedido="Informe o valor para ver o efeito no contrato."
          />
          <Notice icon={Icons.Undo2}>
            O lançamento atual é estornado e o novo entra no livro-razão no mesmo ato. O errado
            continua no razão, riscado, ao lado do certo.
          </Notice>
        </>
      ) : null}

      <TextAreaField
        label="Motivo da correção"
        required
        rows={3}
        hint="Fica no relatório e na auditoria. Mínimo de 10 caracteres."
        value={motivo}
        onChange={(evento) => {
          setMotivo(evento.target.value);
          mexeu('reason');
        }}
        {...erroDe('reason')}
      />
    </>
  ) : tipo ? (
    <>
      <Bloco titulo="Tipo de registro" onAlterar={() => irPara(0)}>
        <div className="flex items-center gap-3">
          <QuadroDoTipo tipo={tipo} grande />
          <div>
            <p className="text-[15px] font-semibold tracking-tight">
              {REPORT_TYPE_META[tipo].label}
            </p>
            <p className="text-[13px] text-ink-3">{REPORT_TYPE_META[tipo].hint}</p>
          </div>
        </div>
      </Bloco>

      <Bloco titulo="Dados" onAlterar={() => irPara(1)}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Dado rotulo="Título" largo>
            {titulo.trim()}
          </Dado>
          <Dado rotulo="Data da visita">{formatDate(dataDaVisita)}</Dado>
          {tipo !== 'visita' ? (
            <Dado rotulo="Data da movimentação">{formatDate(dataDoAto || null)}</Dado>
          ) : null}
          <Dado rotulo="Descrição" largo>
            {descricao.trim() ? (
              <span className="line-clamp-4 whitespace-pre-line text-ink-2">
                {descricao.trim()}
              </span>
            ) : (
              <span className="text-ink-3">Sem descrição.</span>
            )}
          </Dado>
        </dl>
      </Bloco>

      {meta?.effect !== 'none' ? (
        <EfeitoNoContrato
          previsao={previsao}
          tom={tom}
          pedido="Informe o valor para ver o efeito no contrato."
        />
      ) : null}

      <Bloco titulo="Arquivos" onAlterar={() => irPara(2)}>
        {arquivos.length === 0 ? (
          <p className="text-[13px] text-ink-3">Nenhum arquivo anexado.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <ul className="flex flex-wrap gap-1.5">
              {arquivos.slice(0, 10).map((item) => (
                <li
                  key={item.id}
                  title={item.nome}
                  className="grid size-14 place-items-center overflow-hidden border border-line bg-sunken"
                >
                  {item.previa ? (
                    <img src={item.previa} alt="" className="size-full object-cover" />
                  ) : (
                    <Selo extension={extensionOf(item.nome)} />
                  )}
                </li>
              ))}
              {arquivos.length > 10 ? (
                <li className="tabular grid size-14 place-items-center border border-line bg-surface-2 text-[13px] text-ink-3">
                  +{arquivos.length - 10}
                </li>
              ) : null}
            </ul>
            <p className="text-[13px] text-ink-3">
              {plural(enviados.length, 'arquivo vai', 'arquivos vão')} com o relatório
              {enviando ? '; ainda há arquivos subindo' : ''}
              {comErroNoEnvio.length > 0
                ? `; ${plural(comErroNoEnvio.length, 'arquivo falhou e fica', 'arquivos falharam e ficam')} de fora`
                : ''}
              .
            </p>
          </div>
        )}
      </Bloco>

      <Notice icon={Icons.Info}>
        Ao finalizar, o relatório entra no histórico da obra e conta como visita a campo
        {meta?.effect !== 'none' ? ', e a movimentação entra no livro-razão' : ''}.
        {naoResponde ? ` Fica registrado em substituição a ${obra.responsibleFiscal?.name}.` : ''}
      </Notice>
    </>
  ) : null;

  const conteudo =
    etapaId === 'tipo'
      ? etapaTipo
      : etapaId === 'dados'
        ? etapaDados
        : etapaId === 'arquivos'
          ? etapaArquivos
          : etapaRevisao;

  return (
    <>
      {/*
       * A confirmação de saída, por cima do assistente. Irmã do modal, e não
       * filha: o `<dialog>` de cima fica sozinho na camada do topo, e o de baixo
       * fica inerte até ela fechar. `Esc` e o clique fora dela voltam ao
       * preenchimento, como "Continuar".
       *
       * Vem antes do assistente na árvore. Quando as duas fecham juntas ("Sair e
       * guardar", "Descartar"), ela fecha primeiro e devolve o foco ao
       * assistente, que então o devolve ao botão que abriu tudo; na ordem
       * inversa, o foco se perdia no corpo da página.
       */}
      <Modal
        open={open && confirmandoSaida}
        onClose={() => setConfirmandoSaida(false)}
        title={corrigindo ? 'Sair sem salvar a correção?' : 'Sair sem finalizar o relatório?'}
        focoInicial="[data-foco-inicial]"
        footer={
          /*
           * No celular, um botão por linha: o de ficar embaixo, onde o polegar
           * chega primeiro, e o de descartar no alto, longe do toque sem querer.
           */
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
            <Button variant="danger" onClick={descartar} className="sm:mr-auto">
              {corrigindo ? 'Descartar alterações' : 'Descartar rascunho'}
            </Button>
            {corrigindo ? null : (
              <Button variant="secondary" onClick={sairGuardando}>
                Sair e guardar
              </Button>
            )}
            <Button variant="primary" data-foco-inicial onClick={() => setConfirmandoSaida(false)}>
              {corrigindo ? 'Continuar corrigindo' : 'Continuar preenchendo'}
            </Button>
          </div>
        }
      >
        {corrigindo ? (
          <p className="text-[14px] text-ink-2">
            As alterações deste relatório ainda não foram salvas. Ao sair, elas são descartadas, e o
            relatório continua como está.
          </p>
        ) : (
          <div className="flex flex-col gap-3 text-[14px] text-ink-2">
            <p>
              O que foi preenchido fica guardado como rascunho neste navegador e volta quando o Novo
              relatório desta obra for aberto de novo.
            </p>
            {enviando ? (
              <p className="text-[13px] text-warning">
                Há arquivos ainda subindo: eles ficam de fora do rascunho e precisam ser escolhidos
                de novo.
              </p>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal
        open={open}
        onClose={pedirParaFechar}
        onFechadoPeloNavegador={fechadoPeloNavegador}
        focoInicial={FOCO_AO_ABRIR}
        size="xl"
        flush
        title={corrigindo ? 'Corrigir relatório' : 'Novo relatório'}
        description={corrigindo ? report?.title : obra.name}
        footer={
          <>
            <span className="tabular mr-auto hidden text-[13px] text-ink-3 sm:inline">
              Etapa {indice + 1} de {etapas.length}
            </span>
            {indice === 0 ? (
              <Button variant="ghost" onClick={pedirParaFechar} disabled={salvando}>
                Cancelar
              </Button>
            ) : (
              <Button
                variant="ghost"
                icon={<ArrowLeft {...ICON} size={16} />}
                onClick={() => irPara(indice - 1)}
                disabled={salvando}
              >
                Voltar
              </Button>
            )}
            {ultima ? (
              <BotaoFinalizar
                key={aberturas}
                bloqueio={bloqueioDoLancamento}
                salvando={salvando}
                onFinalizar={() => void lancar()}
              />
            ) : (
              <Button variant="primary" onClick={avancar} icon={<ArrowRight {...ICON} size={16} />}>
                Continuar
              </Button>
            )}
          </>
        }
      >
        {/*
         * Altura fixa a partir de `sm`, como no cadastro de obra: o rodapé não
         * sobe e desce a cada etapa, e quem rola é a coluna da etapa. No celular a
         * altura é livre, e a trilha vira a barra de segmentos no topo.
         */}
        <div className="grid border-t border-line sm:h-[560px] sm:grid-cols-[232px_minmax(0,1fr)]">
          <aside className="border-line bg-surface-2 px-5 py-4 max-sm:border-b sm:border-r sm:py-6">
            <TrilhaDeEtapas
              rotulo={corrigindo ? 'Etapas da correção' : 'Etapas do relatório'}
              etapas={trilha}
              atual={indice}
              onIr={irPelaTrilha}
            />
          </aside>

          <form
            ref={formRef}
            onSubmit={enviarFormulario}
            noValidate
            className="min-w-0 sm:overflow-y-auto"
          >
            <AnimatePresence
              mode="wait"
              initial={false}
              custom={direcao}
              onExitComplete={() => formRef.current?.scrollTo({ top: 0 })}
            >
              <motion.div
                key={etapaId}
                custom={direcao}
                variants={DESLIZE}
                initial="entra"
                animate="parado"
                exit="sai"
                data-etapa
                ref={(elemento: HTMLDivElement | null) => {
                  // A etapa que entra depois de uma troca leva o foco ao primeiro campo.
                  if (!elemento || !focarAoEntrar.current) return;
                  focarAoEntrar.current = false;
                  focarNaEtapa(elemento);
                }}
                className="flex flex-col gap-6 px-6 py-6 sm:px-8"
              >
                <header>
                  <p className="tabular text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
                    Etapa {indice + 1} de {etapas.length}
                  </p>
                  <h3
                    data-titulo-da-etapa
                    tabIndex={-1}
                    className="mt-1 text-[18px] font-semibold tracking-tight outline-none"
                  >
                    {cabecalho.titulo}
                  </h3>
                  <p className="mt-1 max-w-[62ch] text-[13px] text-ink-3">{cabecalho.descricao}</p>
                </header>

                {recuperadoEm ? (
                  <Notice tone="primary" icon={Icons.History}>
                    <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <span>
                        <strong className="font-semibold">Rascunho recuperado</strong>, guardado em{' '}
                        {formatDateTime(recuperadoEm)}. O preenchimento continua de onde parou.
                      </span>
                      <button
                        type="button"
                        onClick={descartarRascunho}
                        className="text-[12px] font-medium underline underline-offset-2 transition-colors hover:text-primary"
                      >
                        Descartar rascunho
                      </button>
                    </span>
                  </Notice>
                ) : null}

                {erroGeral ? <FormError>{erroGeral}</FormError> : null}

                {conteudo}
              </motion.div>
            </AnimatePresence>
          </form>
        </div>
      </Modal>
    </>
  );
}
